import { recordError } from "@/services/record-error";
import { useStore } from "@/services/store";

import { QueryCache, QueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

// Shared server-state cache (ADR 0043). Every screen reads through `useQuery`
// with `api` as the query function; `api` already handles timeouts, the POST
// retry, session expiry and Sentry, so TanStack Query adds only the cache.
export function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      // One place for the load error a screen used to show in its own
      // useEffect catch: `meta.errorMessage` keeps the screen's French
      // message, otherwise the API code is shown like everywhere else.
      // `meta.silent` is for previews that must never show an error, and
      // `meta.capture: false` for reads that must stay out of Sentry, like
      // `api`'s own `capture: false` option.
      onError: (error, query) => {
        if (query.meta?.capture !== false)
          recordError(error, {
            source: query.meta?.source || "query-client",
            operation: JSON.stringify(query.queryKey),
          });
        if (query.meta?.silent) return;
        // A 401 is a session problem, handled once for the whole app (session
        // expired message and redirect). It also happens when the screen being
        // left reloads right after a logout or a « se connecter à ce compte ».
        if (error?.status === 401) return;
        // `meta.apiMessage` shows the French message the API attaches, for the
        // screens where AGENTS.md asks for it (admin editorial).
        if (query.meta?.apiMessage)
          return toast.error(error?.message || error?.code || "API_ERROR");
        toast.error(query.meta?.errorMessage || error?.code || "API_ERROR");
      },
    }),
    defaultOptions: {
      queries: {
        // `api` retries what can be retried; a second layer would multiply
        // requests and delay the error toast.
        retry: false,
        // Data stays fresh for 30 s: going back to a screen shows the cached
        // data instantly and refreshes it in the background after that.
        staleTime: 30 * 1000,
        // A therapist switching tabs in the middle of an edit must not see
        // the screen reload under their hands.
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

// `api` resolves `{ ok: false }` for some failures instead of throwing: the
// query must then fail so the screen gets its error state and toast.
export async function queryResponse(request) {
  const response = await request;
  if (!response?.ok) throw { ...response, code: response?.code || "API_ERROR" };
  return response;
}

export const queryClient = createQueryClient();

// The cache belongs to one account: logout, a session expiry, an admin
// impersonation or switching role must never show the previous account's
// data. A plan change also clears it, because several therapist reads (Stripe
// status, offers) answer differently for Free, Basic and Premium. The
// sessions themselves (`/<role>/signin_token`) stay.
function isSessionQuery(query) {
  return String(query.queryKey[0]).endsWith("/signin_token");
}

function accountKey() {
  const role = useStore.getState().activeRole;
  if (!role) return "";
  const user = queryClient.getQueryData([`/${role}/signin_token`])?.user;
  return `${role}:${user?._id || ""}:${user?.plan || ""}`;
}

let currentAccountKey = "";

function clearOnAccountChange() {
  const nextAccountKey = accountKey();
  if (nextAccountKey === currentAccountKey) return;
  const previousAccountKey = currentAccountKey;
  currentAccountKey = nextAccountKey;
  // Signing in from no account keeps the cache: it only holds public reads,
  // like the guide a server-rendered page hydrated before the session was
  // restored (removing it would show the loader again on the next render).
  if (!previousAccountKey) return;
  queryClient.removeQueries({ predicate: (query) => !isSessionQuery(query) });
}

useStore.subscribe(clearOnAccountChange);
queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== "updated") return;
  if (!isSessionQuery(event.query)) return;
  clearOnAccountChange();
});
